import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { TaskScreenSkeleton } from '@/pages/task/task-route-page';
import { ApiError, useTask, useTeams, useTeamProjects } from '@/shared/api';
import { useMobile } from '@/shared/lib/use-mobile';
import { MobileErrorState } from '@/shared/ui/mobile-error-state';
import common from '@/shared/i18n/en/common';
import messages from '@/shared/i18n/en/shell';
import { useT } from '@/shared/i18n/react';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * Generic `/tasks/$taskId` resolver — opens any task by id from anywhere
 * (mention chips, notifications, etc.) and redirects to the canonical
 * shareable deep-link `/teams/$/projects/$/$taskKey`, which renders the
 * desktop modal or, on a phone, the full-screen mobile task card.
 *
 * On 403/404 from `useTask`, redirects to `/access-denied`.  The actor's
 * RLS-filtered view is the source of truth — no team / project information
 * leaks via this route.
 */
function TaskByIdPage() {
  const t = useT(messages);
  const tc = useT(common);
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { taskId } = useParams({ from: '/tasks/$taskId' });
  const taskQuery = useTask(taskId);
  const teamsQuery = useTeams();
  const teams = teamsQuery.data ?? [];
  const task = taskQuery.data;
  const teamSlug = task ? teams.find((t) => t.id === task.teamId)?.slug : undefined;
  const projectsQuery = useTeamProjects(teamSlug);
  const projects = projectsQuery.data ?? [];
  const projectSlug = task ? projects.find((p) => p.id === task.projectId)?.slug : undefined;

  const isDenied =
    taskQuery.error instanceof ApiError &&
    (taskQuery.error.status === 403 || taskQuery.error.status === 404);

  useEffect(() => {
    if (isDenied) {
      void navigate({ to: '/access-denied', replace: true });
      return;
    }
    if (task && teamSlug && projectSlug) {
      void navigate({
        to: '/teams/$teamSlug/projects/$projectSlug/$taskKey',
        params: { teamSlug, projectSlug, taskKey: task.key },
        replace: true,
      });
    }
  }, [isDenied, task, teamSlug, projectSlug, navigate]);

  // A network / server failure (not an access answer) — offer a retry instead
  // of resolving forever.
  const failed =
    taskQuery.isError && !isDenied ? taskQuery : teamsQuery.isError ? teamsQuery : null;
  if (failed) {
    return (
      <MobileErrorState
        className="h-full grid content-center pt-safe"
        title={t('couldntOpenTask')}
        onRetry={() => void failed.refetch()}
        retrying={failed.isFetching}
        action={
          // On a phone the shell's header is hidden on task screens — keep a way out.
          isMobile ? (
            <button
              type="button"
              onClick={() => void navigate({ to: '/my/work', replace: true })}
              className="inline-flex items-center min-h-11 rounded-full border border-[var(--border)] px-4 text-caption font-medium text-[var(--fg)]"
            >
              {tc('back')}
            </button>
          ) : undefined
        }
      />
    );
  }

  if (isMobile) {
    return (
      <div className="h-full pt-safe">
        <TaskScreenSkeleton backTo="/my/work" />
      </div>
    );
  }
  return (
    <div className="h-full p-4 space-y-3" aria-busy="true" aria-label={t('openingTask')}>
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-5 w-3/4" />
      <Skeleton variant="card" className="h-32" />
    </div>
  );
}

export const Route = createFileRoute('/tasks/$taskId')({ component: TaskByIdPage });
